import { IconFlask, IconSparkles } from "@tabler/icons-react";
import { useQuery } from "@tanstack/react-query";
import { useEffect, useRef, useState } from "react";
import { PageTabs } from "@/components/patterns/PageTabs";
import { PageHeader } from "@/components/patterns/page-header";
import { SearchInput } from "@/components/patterns/SearchInput";
import { EmptyState } from "@/components/shared/empty-state";
import { ErrorState } from "@/components/shared/error-state";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Spinner } from "@/components/ui/spinner";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { TabsContent } from "@/components/ui/tabs";
import { defaultStrategyName } from "@/lib/strategy-name";
import { strategySnapshotQuery } from "@/lib/strategy-snapshot";
import {
  BUILTIN_STRATEGIES,
  buildProductStrategy,
  type Strategy,
} from "@/mocks/operating-strategy";
import { STRATEGY_PRODUCT } from "@/mocks/strategy-product";
import { useAuthStore } from "@/stores/auth";
import { operatingStrategyScope, useOperatingStrategyStore } from "@/stores/operating-strategy";
import { STRATEGY_SOURCE_LABELS, StrategyDetailSheet } from "./StrategyDetailSheet";

type LabTab = "library" | "distill" | "learn";
interface StrategyLabPageProps {
  category?: Strategy["source"];
  view?: LabTab;
  strategyId?: string;
  onSearchChange: (search: {
    category: Strategy["source"];
    view?: LabTab;
    strategyId?: string;
  }) => void;
}
const categories = ["builtin", "custom", "ai"] as const;
const LEARNING_TACTIC_IDS = ["builtin-01", "builtin-08", "builtin-13", "builtin-20", "builtin-26"];

export function StrategyLabPage(props: StrategyLabPageProps): React.ReactElement {
  const user = useAuthStore((state) => state.user);
  const shops = useAuthStore((state) => state.shops);
  const shopId = useAuthStore((state) => state.currentShopId);
  const scopeKey = useOperatingStrategyStore((state) => state.scopeKey);
  const setScope = useOperatingStrategyStore((state) => state.setScope);
  const expectedScope = operatingStrategyScope(user, shops, shopId);
  useEffect(() => {
    setScope(expectedScope);
  }, [expectedScope, setScope]);
  if (!expectedScope)
    return (
      <EmptyState
        title="请先选择店铺"
        description="选择当前工作空间中的店铺后，即可查看策略实验室。"
      />
    );
  if (scopeKey !== expectedScope)
    return (
      <div className="flex min-h-64 items-center justify-center" role="status">
        <Spinner />
        <span className="sr-only">正在加载策略</span>
      </div>
    );
  return <StrategyLabContent key={`${expectedScope}:${props.view ?? "library"}`} {...props} />;
}

function StrategyLabContent({
  category = "builtin",
  view = "library",
  strategyId,
  onSearchChange,
}: StrategyLabPageProps): React.ReactElement {
  const userId = useAuthStore((state) => state.user?.id);
  const shops = useAuthStore((state) => state.shops);
  const hasShops = shops.length > 0;
  const addStrategy = useOperatingStrategyStore((state) => state.addStrategy);
  const {
    data: snapshot,
    isPending: snapshotPending,
    isError: snapshotError,
    refetch,
  } = useQuery({
    ...strategySnapshotQuery(
      userId ?? "",
      shops.map((shop) => shop.shopId),
    ),
    enabled: Boolean(category === "ai" && userId && hasShops),
  });
  useEffect(() => {
    if (!snapshot) return;
    addStrategy(
      buildProductStrategy(snapshot, "ai-b0gg6ytx2h", defaultStrategyName(STRATEGY_PRODUCT.asin)),
    );
  }, [snapshot, addStrategy]);
  const strategies = useOperatingStrategyStore((state) => state.strategies);
  const [query, setQuery] = useState("");
  const [page, setPage] = useState(1);
  const [detail, setDetail] = useState<Strategy | null>(null);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(() => new Set());
  const detailTriggerRef = useRef<HTMLButtonElement | null>(null);
  const allStrategies = [...BUILTIN_STRATEGIES, ...strategies];
  const focusedStrategy = strategyId
    ? (allStrategies.find((item) => item.id === strategyId) ?? null)
    : null;
  const visible = (
    category === "builtin"
      ? BUILTIN_STRATEGIES
      : strategies.filter((item) => item.source === category)
  ).filter((item) => item.name.toLowerCase().includes(query.trim().toLowerCase()));
  const pageCount = Math.max(1, Math.ceil(visible.length / 8));
  const safePage = Math.min(page, pageCount);
  const customStrategies = strategies.filter((item) => item.source === "custom");
  const distill = view === "distill";
  const learningStrategies = BUILTIN_STRATEGIES.filter((item) =>
    LEARNING_TACTIC_IDS.includes(item.id),
  );
  const selectableStrategies = distill ? customStrategies : learningStrategies;
  const selectedCount = selectableStrategies.filter((item) => selectedIds.has(item.id)).length;
  const title =
    category === "builtin"
      ? "内置广告策略"
      : category === "custom"
        ? "你的自定义策略"
        : "AI 生成的策略";
  const description =
    category === "builtin"
      ? "浏览可复用的广告打法，了解优势、限制与适用情形。"
      : category === "custom"
        ? "保留你制定的运营方案，在实验室中继续探索。"
        : "查看根据商品与业务目标生成的运营方案。";
  function changeCategory(next: Strategy["source"]): void {
    setQuery("");
    setPage(1);
    setDetail(null);
    onSearchChange({ category: next, view });
  }
  return (
    <main className="flex min-h-full flex-col gap-6 px-8 py-7">
      <PageHeader
        title="策略实验室"
        description="沉淀可复用的广告方法，让每一次运营决策都有据可循。"
        actions={<Badge variant="neutral">策略资产</Badge>}
      />
      {view === "library" ? (
        <PageTabs
          value={category}
          onValueChange={(next) => {
            if (categories.some((item) => item === next)) {
              changeCategory(next as Strategy["source"]);
            }
          }}
          items={categories.map((item) => ({ value: item, label: STRATEGY_SOURCE_LABELS[item] }))}
          action={
            <SearchInput
              showIcon
              className="h-9 w-[260px]"
              placeholder="搜索策略名称"
              value={query}
              onChange={(event) => {
                setQuery(event.target.value);
                setPage(1);
              }}
            />
          }
        >
          <TabsContent value={category} className="space-y-6">
            <div className="flex items-center justify-between gap-4">
              <div>
                <h2 className="text-lg font-semibold">{title}</h2>
                <p className="mt-1.5 text-sm text-muted-foreground">{description}</p>
              </div>
              {category === "builtin" && <Badge variant="neutral">全部用户可查看</Badge>}
            </div>
            <div className="overflow-hidden rounded-[10px] border border-line bg-surface">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-[36%] pl-[18px]">策略名称</TableHead>
                    <TableHead>来源</TableHead>
                    <TableHead>适用目标</TableHead>
                    <TableHead>操作</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {visible.slice((safePage - 1) * 8, safePage * 8).map((strategy) => (
                    <TableRow key={strategy.id} className="h-[62px]">
                      <TableCell className="pl-[18px]">
                        <div className="font-medium">{strategy.name}</div>
                        <div className="mt-1 text-xs text-muted-foreground">
                          {category === "builtin"
                            ? `打法 ${strategy.id.replace("builtin-", "")}`
                            : `ASIN · ${strategy.asin || "暂无"}`}
                        </div>
                      </TableCell>
                      <TableCell>
                        <Badge variant="neutral">
                          {category === "builtin"
                            ? "内置"
                            : category === "custom"
                              ? "自定义"
                              : "AI 生成"}
                        </Badge>
                      </TableCell>
                      <TableCell>{strategy.goal}</TableCell>
                      <TableCell>
                        <Button
                          variant="outline"
                          onClick={(event) => {
                            detailTriggerRef.current = event.currentTarget;
                            setDetail(strategy);
                          }}
                        >
                          查看详情
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                  {visible.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={4} className="h-44 text-center">
                        {snapshotError && category === "ai" && hasShops ? (
                          <ErrorState
                            message="商品策略资料加载失败，请重试。"
                            onRetry={() => void refetch()}
                            variant="inline"
                          />
                        ) : (
                          <>
                            <p className="font-medium">
                              {snapshotPending && category === "ai" && hasShops
                                ? "正在载入商品策略"
                                : query
                                  ? "未找到匹配的策略"
                                  : "暂无策略"}
                            </p>
                            <p className="mt-2 text-muted-foreground">
                              {query
                                ? "尝试其他关键词，或清空搜索条件。"
                                : "完成 AI 运营策略创建后，可在这里查看已保存的方案。"}
                            </p>
                          </>
                        )}
                        {query && (
                          <Button
                            variant="ghost"
                            className="mt-3"
                            onClick={() => {
                              setQuery("");
                              setPage(1);
                            }}
                          >
                            清空搜索
                          </Button>
                        )}
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
              <div className="flex items-center justify-between gap-4 border-t border-line bg-table-footer px-[18px] py-3 text-xs text-muted-foreground">
                <span>
                  第 {visible.length ? (safePage - 1) * 8 + 1 : 0}–
                  {Math.min(safePage * 8, visible.length)} 条，共 {visible.length} 条
                </span>
                <nav aria-label="策略列表分页" className="flex items-center gap-3">
                  <Button
                    variant="outline"
                    disabled={safePage === 1}
                    onClick={() => setPage(safePage - 1)}
                  >
                    上一页
                  </Button>
                  <span>
                    {safePage} / {pageCount}
                  </span>
                  <Button
                    variant="outline"
                    disabled={safePage === pageCount}
                    onClick={() => setPage(safePage + 1)}
                  >
                    下一页
                  </Button>
                </nav>
              </div>
            </div>
          </TabsContent>
        </PageTabs>
      ) : (
        <section className="space-y-6">
          <div className="flex items-center justify-between gap-4">
            <div>
              <h2 className="text-lg font-semibold">
                {distill ? "从实战方案中提炼可复用策略" : "从内置广告打法中学习"}
              </h2>
              <p className="mt-1.5 text-sm text-muted-foreground">
                {distill ? "选择多条自定义策略。" : "选择多条内置广告打法。"}
              </p>
            </div>
          </div>
          <div className="overflow-hidden rounded-[10px] border border-line bg-surface">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-16">选择</TableHead>
                  <TableHead>{distill ? "策略名称" : "打法名称"}</TableHead>
                  <TableHead>{distill ? "关联商品" : "适用目标"}</TableHead>
                  <TableHead>操作</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {selectableStrategies.map((strategy) => (
                  <TableRow
                    key={strategy.id}
                    className="h-[70px]"
                    data-state={selectedIds.has(strategy.id) ? "selected" : undefined}
                  >
                    <TableCell>
                      <Checkbox
                        aria-label={`选择${strategy.name}`}
                        checked={selectedIds.has(strategy.id)}
                        onCheckedChange={(checked) => {
                          setSelectedIds((current) => {
                            const next = new Set(current);
                            if (checked) next.add(strategy.id);
                            else next.delete(strategy.id);
                            return next;
                          });
                        }}
                      />
                    </TableCell>
                    <TableCell>
                      <div className="font-medium">{strategy.name}</div>
                      <div className="mt-1 text-xs text-muted-foreground">
                        {distill ? strategy.goal : `打法 ${strategy.id.replace("builtin-", "")}`}
                      </div>
                    </TableCell>
                    <TableCell>{distill ? strategy.asin || "暂无" : strategy.goal}</TableCell>
                    <TableCell>
                      <Button
                        variant="outline"
                        onClick={(event) => {
                          detailTriggerRef.current = event.currentTarget;
                          setDetail(strategy);
                        }}
                      >
                        查看详情
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
                {selectableStrategies.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={4} className="h-32 text-center text-muted-foreground">
                      {distill ? "暂无自定义策略" : "暂无内置广告打法"}
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </div>
        </section>
      )}
      {view !== "library" && (
        <footer className="sticky bottom-0 mt-auto flex items-center justify-between border-t border-line bg-surface py-5">
          <p className="text-sm text-muted-foreground">
            已选择 {selectedCount} 条{distill ? "自定义策略" : "内置广告打法"}
          </p>
          <Button disabled={selectedCount === 0}>
            {distill ? <IconFlask /> : <IconSparkles />}
            {distill ? "一键蒸馏" : "一键升级"}
          </Button>
        </footer>
      )}
      <StrategyDetailSheet
        returnFocus={detailTriggerRef}
        strategy={detail ?? focusedStrategy}
        onClose={() => {
          setDetail(null);
          if (strategyId) onSearchChange({ category, view });
        }}
      />
    </main>
  );
}
